<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { APPOINTMENT_FIELDS as F } from '../appointmentFields';

const props = defineProps({
  customAttributes: { type: Object, default: () => ({}) },
  taskTitle: { type: String, default: '' },
  saving: { type: Boolean, default: false },
  deferred: { type: Boolean, default: false },
  showConsultationNumber: { type: Boolean, default: true },
  showRecurrence: { type: Boolean, default: true },
});
const emit = defineEmits(['save']);
const { t } = useI18n();

const date = ref('');
const hour = ref('14');
const minute = ref('00');
const durationHours = ref('1');
const durationMinutes = ref('00');
const title = ref('');
const mode = ref('Online');
const recurrence = ref('Não repetir');
const consultationNumber = ref(1);
const outcome = ref('realizada');
const history = ref([]);
const error = ref('');

const hours = Array.from({ length: 24 }, (_, index) =>
  String(index).padStart(2, '0')
);
const minutes = Array.from({ length: 12 }, (_, index) =>
  String(index * 5).padStart(2, '0')
);
const durationHourOptions = Array.from({ length: 9 }, (_, index) =>
  String(index)
);
const outcomes = ['realizada', 'reagendada', 'nao_compareceu'];

const parseDate = value => {
  if (typeof value === 'number') {
    return new Date(value > 10000000000 ? value : value * 1000)
      .toISOString()
      .slice(0, 10);
  }
  if (typeof value !== 'string') return '';
  const match = value.match(/^\d{4}-\d{2}-\d{2}/);
  return match?.[0] || '';
};

const parseHistory = value => {
  if (!value) return [];
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return Array.isArray(parsed) ? parsed.filter(item => item?.id) : [];
  } catch {
    return [];
  }
};

const completedCount = computed(
  () => history.value.filter(item => item.status === 'realizada').length
);
const nextConsultationNumber = computed(() => completedCount.value + 1);

watch(
  () => props.customAttributes,
  attrs => {
    const source = attrs || {};
    date.value = parseDate(source[F.date]);
    const time = String(source[F.time] || '14:00').match(/^(\d{1,2}):(\d{2})$/);
    hour.value = time ? String(Number(time[1])).padStart(2, '0') : '14';
    minute.value = time && minutes.includes(time[2]) ? time[2] : '00';
    const duration = Math.max(5, Number(source[F.duration]) || 60);
    durationHours.value = String(Math.floor(duration / 60));
    durationMinutes.value = String(duration % 60).padStart(2, '0');
    if (!minutes.includes(durationMinutes.value)) durationMinutes.value = '00';
    title.value = String(source[F.title] || props.taskTitle || '');
    mode.value = source[F.mode] === 'Presencial' ? 'Presencial' : 'Online';
    recurrence.value =
      props.showRecurrence && source[F.recurrence] === 'Semanal'
        ? 'Semanal'
        : 'Não repetir';
    history.value = parseHistory(source[F.history]);
    const storedNumber = Number(source[F.consultationNumber]);
    consultationNumber.value = props.showConsultationNumber
      ? Math.max(
          Number.isInteger(storedNumber) && storedNumber > 0 ? storedNumber : 1,
          completedCount.value + 1
        )
      : 1;
    error.value = '';
  },
  { immediate: true, deep: true }
);

const duration = computed(
  () => Number(durationHours.value) * 60 + Number(durationMinutes.value)
);
const time = computed(() => `${hour.value}:${minute.value}`);

const appointmentKey = (itemDate, itemTime, number) =>
  `${itemDate}|${itemTime}|${props.showConsultationNumber ? number : ''}`;

const scheduledHistory = () => {
  const items = history.value.map(item => ({ ...item }));
  const number = Number(consultationNumber.value);
  const key = appointmentKey(date.value, time.value, number);
  const current = [...items]
    .reverse()
    .find(
      item =>
        item.status === 'agendada' &&
        (!props.showConsultationNumber ||
          Number(item.consultation_number) === number)
    );
  if (current && appointmentKey(current.date, current.time, number) !== key) {
    current.status = 'reagendada';
    current.recorded_at = new Date().toISOString();
  }
  if (
    !items.some(
      item =>
        appointmentKey(
          item.date,
          item.time,
          Number(item.consultation_number)
        ) === key
    )
  ) {
    const item = {
      id: `${Date.now()}-${number}`,
      date: date.value,
      time: time.value,
      duration_minutes: duration.value,
      title: title.value.trim(),
      status: 'agendada',
      recorded_at: new Date().toISOString(),
    };
    if (props.showConsultationNumber) item.consultation_number = number;
    if (props.showRecurrence) item.recurrence = recurrence.value;
    items.push(item);
  }
  return items;
};

const appointmentAttributes = items => {
  const attributes = {
    ...props.customAttributes,
    [F.date]: date.value,
    [F.time]: time.value,
    [F.duration]: duration.value,
    [F.title]: title.value.trim(),
    [F.mode]: mode.value,
    [F.history]: JSON.stringify(items),
  };
  if (props.showConsultationNumber) {
    attributes[F.type] =
      Number(consultationNumber.value) === 1
        ? 'Primeira consulta'
        : 'Consulta de acompanhamento';
    attributes[F.consultationNumber] = Number(consultationNumber.value);
  }
  if (props.showRecurrence) attributes[F.recurrence] = recurrence.value;
  return attributes;
};

const saveAppointment = () => {
  if (!date.value || !title.value.trim() || duration.value < 5) {
    error.value = t('KANBAN_APPOINTMENT.REQUIRED');
    return;
  }
  error.value = '';
  const items = scheduledHistory();
  history.value = items;
  emit('save', {
    customAttributes: appointmentAttributes(items),
    syncDates: true,
  });
};

const registerOutcome = () => {
  if (!date.value || !outcomes.includes(outcome.value)) {
    error.value = t('KANBAN_APPOINTMENT.REQUIRED');
    return;
  }
  const items = scheduledHistory();
  const number = Number(consultationNumber.value);
  const active = [...items]
    .reverse()
    .find(
      item =>
        item.status === 'agendada' &&
        (!props.showConsultationNumber ||
          Number(item.consultation_number) === number)
    );
  if (active) {
    active.status = outcome.value;
    active.recorded_at = new Date().toISOString();
  }
  history.value = items;
  const attributes = {
    ...props.customAttributes,
    [F.history]: JSON.stringify(items),
    [F.lastOutcome]: outcome.value,
  };
  const isRescheduled = outcome.value === 'reagendada';
  if (isRescheduled) attributes[F.date] = '';
  emit('save', {
    customAttributes: attributes,
    syncDates: false,
    clearDates: isRescheduled,
  });
  if (props.showConsultationNumber && outcome.value === 'realizada') {
    consultationNumber.value = number + 1;
  }
};

const formatHistoryDate = item =>
  `${String(item.date || '')
    .split('-')
    .reverse()
    .join('/')} ${item.time || ''}`;
const statusLabel = status => {
  if (status === 'realizada') return t('KANBAN_APPOINTMENT.STATUS.realizada');
  if (status === 'reagendada') return t('KANBAN_APPOINTMENT.STATUS.reagendada');
  if (status === 'nao_compareceu')
    return t('KANBAN_APPOINTMENT.STATUS.nao_compareceu');
  return t('KANBAN_APPOINTMENT.STATUS.agendada');
};
const saveLabel = computed(() =>
  props.deferred ? t('KANBAN_APPOINTMENT.APPLY') : t('KANBAN_APPOINTMENT.SAVE')
);
const progressLabel = computed(() =>
  t('KANBAN_APPOINTMENT.PROGRESS', {
    completed: completedCount.value,
    next: nextConsultationNumber.value,
  })
);
const historyTitle = item =>
  props.showConsultationNumber
    ? t('KANBAN_APPOINTMENT.HISTORY_TITLE', {
        number: item.consultation_number,
        title: item.title,
      })
    : t('KANBAN_APPOINTMENT.HISTORY_TITLE_NO_NUMBER', {
        title: item.title,
      });
const historyMeta = item =>
  t('KANBAN_APPOINTMENT.HISTORY_META', {
    date: formatHistoryDate(item),
    duration: item.duration_minutes,
  });
</script>

<template>
  <div class="flex flex-col gap-5">
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Input
        v-model="date"
        data-test="appointment-date"
        type="date"
        :label="t('KANBAN_APPOINTMENT.DATE')"
      />
      <Input
        v-model="title"
        data-test="appointment-title"
        :label="t('KANBAN_APPOINTMENT.TITLE')"
        :placeholder="t('KANBAN_APPOINTMENT.TITLE_PLACEHOLDER')"
      />
    </div>

    <div
      class="grid grid-cols-1 gap-4"
      :class="showConsultationNumber ? 'sm:grid-cols-3' : 'sm:grid-cols-2'"
    >
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('KANBAN_APPOINTMENT.TIME') }}
        <span class="grid grid-cols-2 gap-2">
          <select
            v-model="hour"
            data-test="appointment-hour"
            class="h-10 rounded-lg bg-n-alpha-black2 px-3 outline outline-1 outline-n-weak"
          >
            <option v-for="item in hours" :key="item" :value="item">
              {{ t('KANBAN_APPOINTMENT.HOURS', { value: item }) }}
            </option>
          </select>
          <select
            v-model="minute"
            data-test="appointment-minute"
            class="h-10 rounded-lg bg-n-alpha-black2 px-3 outline outline-1 outline-n-weak"
          >
            <option v-for="item in minutes" :key="item" :value="item">
              {{ t('KANBAN_APPOINTMENT.MINUTES', { value: item }) }}
            </option>
          </select>
        </span>
      </label>
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('KANBAN_APPOINTMENT.DURATION') }}
        <span class="grid grid-cols-2 gap-2">
          <select
            v-model="durationHours"
            data-test="duration-hours"
            class="h-10 rounded-lg bg-n-alpha-black2 px-3 outline outline-1 outline-n-weak"
          >
            <option
              v-for="item in durationHourOptions"
              :key="item"
              :value="item"
            >
              {{ t('KANBAN_APPOINTMENT.HOURS', { value: item }) }}
            </option>
          </select>
          <select
            v-model="durationMinutes"
            data-test="duration-minutes"
            class="h-10 rounded-lg bg-n-alpha-black2 px-3 outline outline-1 outline-n-weak"
          >
            <option v-for="item in minutes" :key="item" :value="item">
              {{ t('KANBAN_APPOINTMENT.MINUTES', { value: item }) }}
            </option>
          </select>
        </span>
      </label>
      <Input
        v-if="showConsultationNumber"
        v-model="consultationNumber"
        data-test="consultation-number"
        type="number"
        min="1"
        :label="t('KANBAN_APPOINTMENT.NUMBER')"
      />
    </div>

    <div
      class="grid grid-cols-1 gap-4"
      :class="showRecurrence ? 'sm:grid-cols-2' : 'sm:grid-cols-1'"
    >
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('KANBAN_APPOINTMENT.MODE') }}
        <select
          v-model="mode"
          data-test="appointment-mode"
          class="h-10 rounded-lg bg-n-alpha-black2 px-3 outline outline-1 outline-n-weak"
        >
          <option value="Online">{{ t('KANBAN_APPOINTMENT.ONLINE') }}</option>
          <option value="Presencial">
            {{ t('KANBAN_APPOINTMENT.IN_PERSON') }}
          </option>
        </select>
      </label>
      <label
        v-if="showRecurrence"
        class="flex flex-col gap-1 text-sm text-n-slate-12"
      >
        {{ t('KANBAN_APPOINTMENT.RECURRENCE') }}
        <select
          v-model="recurrence"
          data-test="appointment-recurrence"
          class="h-10 rounded-lg bg-n-alpha-black2 px-3 outline outline-1 outline-n-weak"
        >
          <option value="Não repetir">
            {{ t('KANBAN_APPOINTMENT.ONCE') }}
          </option>
          <option value="Semanal">{{ t('KANBAN_APPOINTMENT.WEEKLY') }}</option>
        </select>
      </label>
    </div>

    <div
      class="flex items-center gap-3"
      :class="showConsultationNumber ? 'justify-between' : 'justify-end'"
    >
      <span v-if="showConsultationNumber" class="text-sm text-n-slate-11">
        {{ progressLabel }}
      </span>
      <Button
        data-test="appointment-save"
        :label="saveLabel"
        :is-loading="saving"
        @click="saveAppointment"
      />
    </div>
    <p v-if="error" role="alert" class="text-sm text-n-ruby-11">{{ error }}</p>
    <p v-if="deferred" class="text-xs text-n-slate-11">
      {{ t('KANBAN_APPOINTMENT.DEFERRED_HINT') }}
    </p>
    <p v-if="customAttributes[F.syncStatus]" class="text-xs text-n-slate-11">
      {{
        t('KANBAN_APPOINTMENT.SYNC_STATUS', {
          status: customAttributes[F.syncStatus],
        })
      }}
    </p>

    <div class="border-t border-n-weak pt-4 flex flex-col gap-3">
      <span class="font-medium text-n-slate-12">{{
        t('KANBAN_APPOINTMENT.HISTORY')
      }}</span>
      <div class="flex flex-col gap-2">
        <div
          v-for="item in [...history].reverse()"
          :key="item.id"
          class="rounded-lg bg-n-alpha-black2 p-3 text-sm"
        >
          <div class="flex justify-between gap-3">
            <span>{{ historyTitle(item) }}</span>
            <span class="text-n-slate-11">{{ statusLabel(item.status) }}</span>
          </div>
          <span class="text-xs text-n-slate-11">{{ historyMeta(item) }}</span>
        </div>
        <span v-if="!history.length" class="text-sm text-n-slate-11">{{
          t('KANBAN_APPOINTMENT.NO_HISTORY')
        }}</span>
      </div>
      <div class="flex flex-col gap-2 sm:flex-row sm:items-end">
        <label class="flex flex-1 flex-col gap-1 text-sm text-n-slate-12">
          {{ t('KANBAN_APPOINTMENT.OUTCOME') }}
          <select
            v-model="outcome"
            data-test="appointment-outcome"
            class="h-10 rounded-lg bg-n-alpha-black2 px-3 outline outline-1 outline-n-weak"
          >
            <option v-for="item in outcomes" :key="item" :value="item">
              {{ statusLabel(item) }}
            </option>
          </select>
        </label>
        <Button
          data-test="outcome-save"
          variant="outline"
          :label="t('KANBAN_APPOINTMENT.REGISTER')"
          :is-loading="saving"
          @click="registerOutcome"
        />
      </div>
    </div>
  </div>
</template>
